import {MutationTree} from 'vuex';
import {CrudAction, CrudResponseAction} from '@/libs/core/crud/crud-action';
import {CrudEntity} from '@/libs/core/crud/crud-service';
import {CrudStoreState} from '@/libs/core/crud/crud-store-state';
import {
    CreatePayload, DeletePayload, DeleteSuccessPayload, FailedPayload, GetAllPayload, GetAllSuccessPayload, GetGroupedPayload,
    GetGroupedSuccessPayload, GetPayload, GetSumPayload, GetSumSuccessPayload, ItemSuccessPayload, PartialUpdatePayload,
    PartialUpdateSuccessPayload, SetLatestRequestIdPayload, UpdatePayload
} from '@/libs/core/crud/crud-store-payloads';

type State = CrudStoreState<CrudEntity, CrudEntity>;
type Entity = CrudEntity;

function setEntry<F extends 'data' | 'dataQuery' | 'detail' | 'pageInfo' | 'sum' | 'groupedData'>(state: State, field: F, key: string | undefined, value: State[F][string]) {
    state[field] = {
        ...state[field],
        [key as string]: value
    };
}

function setFlag(state: State, flag: 'pending' | 'saving' | 'deleting', value: boolean, key = '') {
    state[flag] = {
        ...state[flag],
        [key]: value
    };
}

export const crudMutations: MutationTree<State> = {
    [CrudAction.GetAll]: (state, payload: GetAllPayload<Entity>) => {
        setEntry(state, 'data', payload.key, []);
        setFlag(state, 'pending', true, payload.key);
    },
    [CrudResponseAction.GetAllSuccess]: (state, payload: GetAllSuccessPayload<Entity>) => {
        setEntry(state, 'data', payload.key, payload.data);
        setEntry(state, 'dataQuery', payload.key, payload.query);
        setEntry(state, 'pageInfo', payload.key, {
            currentPage: payload.currentPage,
            rowCount: payload.rowCount,
            pageCount: payload.pageCount,
            pageSize: payload.pageSize
        });
        setFlag(state, 'pending', false, payload.key);
    },
    [CrudResponseAction.GetAllNoop]: (state, payload: { key: string }) => {
        setFlag(state, 'pending', false, payload.key);
    },
    [CrudResponseAction.GetAllFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'pending', false, payload.key);
    },

    [CrudAction.Get]: (state, payload: GetPayload) => {
        setEntry(state, 'detail', payload.key, null);
        setFlag(state, 'pending', true, payload.key);
    },
    [CrudResponseAction.GetSuccess]: (state, payload: ItemSuccessPayload<Entity>) => {
        setEntry(state, 'detail', payload.key, payload.item);
        setFlag(state, 'pending', false, payload.key);
    },
    [CrudResponseAction.GetFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'pending', false, payload.key);
    },

    [CrudAction.Create]: (state, payload: CreatePayload<Entity>) => {
        setFlag(state, 'saving', true, payload.key);
    },
    [CrudResponseAction.CreateSuccess]: (state, payload: ItemSuccessPayload<Entity>) => {
        setFlag(state, 'saving', false, payload.key);
        state.detail = {
            ...state.detail,
            [payload.key]: payload.item,
            [payload.item.id!]: payload.item
        };
    },
    [CrudResponseAction.CreateFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'saving', false, payload.key);
    },

    [CrudAction.Update]: (state, payload: UpdatePayload<Entity>) => {
        setFlag(state, 'saving', true, payload.key);
    },
    [CrudResponseAction.UpdateSuccess]: (state, payload: ItemSuccessPayload<Entity>) => {
        setEntry(state, 'detail', payload.key, payload.item);
        Object.keys(state.data).forEach(key => {
            const existing = state.data[key].find(item => item.id == payload.item.id);
            if (existing) {
                const items = [...state.data[key]];
                items.splice(items.indexOf(existing), 1, payload.item);
                setEntry(state, 'data', key, items);
            }
        });
        setFlag(state, 'saving', false, payload.key);
    },
    [CrudResponseAction.UpdateFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'saving', false, payload.key);
    },

    [CrudAction.PartialUpdate]: (state, payload: PartialUpdatePayload) => {
        setFlag(state, 'saving', true, payload.key);
    },
    [CrudResponseAction.PartialUpdateSuccess]: (state, payload: PartialUpdateSuccessPayload<Entity, Entity>) => {
        setFlag(state, 'saving', false, payload.key);
        if (state.detail[payload.key]) {
            setEntry(state, 'detail', payload.key, payload.item);
        }
        Object.keys(payload.data).forEach(key => {
            state.data[key] = payload.data[key];
        });
    },
    [CrudResponseAction.PartialUpdateFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'saving', false, payload.key);
    },

    [CrudAction.Delete]: (state, payload: DeletePayload<Entity>) => {
        setFlag(state, 'deleting', true, payload.key);
    },
    [CrudResponseAction.DeleteSuccess]: (state, payload: DeleteSuccessPayload<Entity>) => {
        const deletedIds = payload.data.map(item => item.id);
        const deletedItems = state.data[payload.key].filter(item => deletedIds.indexOf(item.id) > -1);
        if (deletedItems.length) {
            state.data[payload.key] = state.data[payload.key].filter(item => deletedItems.indexOf(item) < 0);
            if (state.detail[payload.key]) {
                deletedItems.forEach(item => state.detail[item.id!] = null);
            }
        }
        setFlag(state, 'deleting', false, payload.key);
    },
    [CrudResponseAction.DeleteFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'deleting', false, payload.key);
    },

    [CrudResponseAction.DownloadSuccess]: () => undefined,
    [CrudResponseAction.DownloadFailed]: () => undefined,

    [CrudAction.GetSum]: (state, payload: GetSumPayload) => {
        setEntry(state, 'sum', payload.key, null);
    },
    [CrudResponseAction.GetSumSuccess]: (state, payload: GetSumSuccessPayload) => {
        setEntry(state, 'sum', payload.key, payload.item);
    },
    [CrudResponseAction.GetSumFailed]: (state, payload: FailedPayload) => {
        setEntry(state, 'sum', payload.key, null);
    },

    [CrudAction.GetGrouped]: (state, payload: GetGroupedPayload) => {
        setEntry(state, 'groupedData', payload.key, []);
        setFlag(state, 'pending', true, payload.key);
    },
    [CrudResponseAction.GetGroupedSuccess]: (state, payload: GetGroupedSuccessPayload) => {
        setEntry(state, 'groupedData', payload.key, payload.data);
        setFlag(state, 'pending', false, payload.key);
    },
    [CrudResponseAction.GetGroupedFailed]: (state, payload: FailedPayload) => {
        setFlag(state, 'pending', false, payload.key);
    },

    [CrudAction.SetLatestRequestId]: (state, payload: SetLatestRequestIdPayload) => {
        state.latestRequestIds = {
            ...state.latestRequestIds,
            [payload.operation]: {
                ...state.latestRequestIds[payload.operation],
                [payload.key]: payload.requestId
            }
        };
    },
};
